/**
 * `/about-us/leadership-team/`: Ryerson's leadership team, a hero and a grid of every leader's
 * portrait, each linking to `./[leaderSlug].tsx`. On any other `NEXT_PUBLIC_COMPANY` the page
 * isn't built.
 *
 * Head tags come from `src/content/ryerson/about-us/leadership-team.ts` → `MetaObject` (whose
 * `PageContent` is the page's site-search entry), the hero copy from
 * `components/Leadership/LeadershipTeamContent.tsx`, and the body is
 * `components/Leadership/LeadershipTeam.tsx`. `getStaticProps` fetches the header and footer data
 * and the leader list (`src/contentful/leader/getContentfulLeaders.ts`) at build time; a
 * Contentful change shows only after the next build.
 *
 * `src/unit-test/AboutUs/LeadershipTeam.spec.tsx` renders the page with empty leader lists as a
 * smoke test.
 */
import React from "react";
import { LeadershipTeamHeroContent } from "@components/Leadership/LeadershipTeamContent";
import LeadershipTeam from "@components/Leadership/LeadershipTeam";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead from "@components/Shared/model/MetaHead";
import { MetaObject } from "@content/ryerson/about-us/leadership-team";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import getContentfulLeaders from "@contentful/leader/getContentfulLeaders";
import { ProcessedContentfulLeadersFieldsByLanguage } from "@contentful/leader/types";

/**
 * Ryerson build only. Fetches the header and footer data and every leader in all three languages.
 * `getContentfulLeaders` rethrows a Contentful failure, which fails the build.
 */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	return {
		props: {
			...headerFooterProps,
			contentfulLeadershipTeam: await getContentfulLeaders(),
		},
	};
};

/** Renders the head tags, hero and portrait grid for the current language. */
const Page: React.FC<
	HeaderPageProps & { contentfulLeadershipTeam: ProcessedContentfulLeadersFieldsByLanguage }
> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<LeadershipTeam
				content={LeadershipTeamHeroContent[language]}
				contentfulLeadershipTeam={props.contentfulLeadershipTeam}
			/>
		</HeaderLayout>
	);
};

export default Page;
